interface PrintGuideModalProps {
  open: boolean;
  /** 인쇄할 오답노트 개수 */
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * 인쇄 버튼을 누르면 뜨는 안내 팝업입니다.
 * 총 페이지 수와 예상 용지 장수(단면/양면)를 함께 보여주고,
 * 실제 출력 설정은 브라우저 인쇄창에서 직접 고르도록 안내합니다.
 */
export default function PrintGuideModal({ open, count, onConfirm, onCancel }: PrintGuideModalProps) {
  if (!open) return null;

  // 오답노트 1개 = A4 1페이지. 단면은 그대로, 양면은 절반(올림)이 예상 용지 장수입니다.
  const totalPages = count;
  const sheetsSingle = count;
  const sheetsDouble = Math.ceil(count / 2);

  return (
    <div className="print-hide fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 px-4">
      <div className="w-full max-w-[420px] rounded-lg border border-background-200 bg-white p-6">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-printer-line text-lg"></i>
          </span>
          <div className="leading-tight">
            <h3 className="font-heading text-base font-bold text-foreground-950">인쇄 안내</h3>
            <p className="text-xs text-foreground-500">오답노트 {count}개를 인쇄합니다</p>
          </div>
        </div>

        <div className="mt-4 rounded-md border border-background-200 bg-background-50 px-4 py-3.5">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-foreground-600">
              <i className="ri-file-list-3-line text-base text-foreground-400"></i>
              총 페이지 수
            </span>
            <span className="font-heading font-bold text-foreground-950">
              {totalPages}
              <span className="ml-0.5 text-xs font-semibold text-foreground-600">페이지</span>
            </span>
          </div>
          <div className="mt-2.5 flex items-center justify-between border-t border-background-200 pt-2.5 text-sm">
            <span className="flex items-center gap-1.5 text-foreground-600">
              <i className="ri-file-copy-2-line text-base text-foreground-400"></i>
              예상 용지 장수
            </span>
            <span className="font-heading font-bold text-foreground-950">
              {sheetsSingle}
              <span className="ml-1 text-xs font-semibold text-foreground-600">장 (양면 {sheetsDouble}장)</span>
            </span>
          </div>
        </div>

        <div className="mt-4 rounded-md border border-accent-200 bg-accent-50 px-4 py-4">
          <p className="flex items-start gap-2 text-sm font-bold leading-relaxed text-accent-900">
            <i className="ri-lightbulb-line mt-0.5 text-base text-accent-700"></i>
            인쇄창에서 칼라/흑백 · 단면/양면 인쇄를 잘 선택해서 인쇄하세요.
          </p>
          <ul className="mt-3 space-y-1.5 pl-6 text-xs leading-relaxed text-accent-800">
            <li className="flex items-center gap-1.5">
              <i className="ri-palette-line text-sm text-accent-600"></i>
              칼라 / 흑백
            </li>
            <li className="flex items-center gap-1.5">
              <i className="ri-file-copy-2-line text-sm text-accent-600"></i>
              단면 / 양면 인쇄
            </li>
          </ul>
        </div>

        <p className="mt-3 text-[11px] leading-relaxed text-foreground-500">
          칼라·양면 같은 실제 출력 설정은 프린터와 브라우저 인쇄창에서 결정돼요.
        </p>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onCancel}
            className="flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100"
          >
            취소
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
          >
            <i className="ri-printer-fill text-base"></i>
            인쇄하기
          </button>
        </div>
      </div>
    </div>
  );
}